import type { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PatchingBillingLine } from '../types/billing';
import {
  formatBillingCurrency,
  formatBillingHoursDisplay,
  formatBillingMoneyPlain,
  sumAndRoundHalfUp2
} from './billingRound';

/** Format YYYY-MM as "Apr 2025" for quarterly cycle labels. */
export function formatPatchingBillingMonthLabel(monthYm: string): string {
  const [yearStr, monthStr] = monthYm.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthYm;
  }
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });
}

export function formatPatchingQuarterCycleLabel(
  cycleStart?: string | null,
  cycleEnd?: string | null
): string | null {
  if (!cycleStart || !cycleEnd) {
    return null;
  }
  return `${formatPatchingBillingMonthLabel(cycleStart)} – ${formatPatchingBillingMonthLabel(cycleEnd)}`;
}

function monthHoursForRow(
  row: PatchingBillingLine['resourceBreakdown'][0],
  monthYm: string
): number {
  const match = row.monthlyHours?.find((m) => m.month === monthYm);
  return match?.totalHours ?? 0;
}

interface RenderPatchingPdfSectionOptions {
  doc: jsPDF;
  lines: PatchingBillingLine[];
  currency: string;
  margin: number;
  tableWidth: number;
  footerAreaHeight: number;
  maxContentY: number;
  startY: number;
}

type PatchingLineWithEngagement = PatchingBillingLine & {
  engagementNumber?: string;
  engagementName?: string;
};

function buildPatchingAmountSubtitle(
  line: PatchingLineWithEngagement,
  currency: string
): string {
  const parts = [`Patching Amount: ${formatBillingCurrency(line.billingAmount, currency)}`];

  const engagementLabel = line.engagementNumber || line.engagementName;
  if (engagementLabel) {
    parts.push(`Engagement No: ${engagementLabel}`);
  }

  if (line.billingCycle === 'QUARTERLY') {
    const cycleLabel = formatPatchingQuarterCycleLabel(
      line.quarterCycleStartMonth,
      line.quarterCycleEndMonth
    );
    if (cycleLabel) {
      parts.push(`Quarterly Cycle : ${cycleLabel}`);
    }
  }

  return parts.join(' | ');
}

function groupPatchingLinesByType(
  lines: PatchingBillingLine[]
): Array<{ patchingTypeLabel: string; lines: PatchingLineWithEngagement[] }> {
  const byType = new Map<string, PatchingLineWithEngagement[]>();
  for (const line of lines) {
    const key = line.patchingType;
    const list = byType.get(key) ?? [];
    list.push(line as PatchingLineWithEngagement);
    byType.set(key, list);
  }
  return Array.from(byType.values()).map((groupLines) => ({
    patchingTypeLabel: groupLines[0].patchingTypeLabel,
    lines: groupLines
  }));
}

export function renderPatchingPdfSections({
  doc,
  lines,
  currency,
  margin,
  tableWidth,
  footerAreaHeight,
  maxContentY,
  startY
}: RenderPatchingPdfSectionOptions): number {
  let yPosition = startY;
  const visibleLines = lines.filter((line) => line.showInPeriod !== false);
  const typeGroups = groupPatchingLinesByType(visibleLines);

  for (const typeGroup of typeGroups) {
    yPosition += 5;
    const billingCycleLabel = typeGroup.lines[0]?.billingCycleLabel;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(typeGroup.patchingTypeLabel, margin, yPosition);
    if (billingCycleLabel) {
      const titleWidth = doc.getTextWidth(typeGroup.patchingTypeLabel);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text(` (${billingCycleLabel})`, margin + titleWidth, yPosition);
    }

    yPosition += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    const quarterlyLine = typeGroup.lines.find((line) => line.billingCycle === 'QUARTERLY');
    const patchingSubtitle = typeGroup.lines
      .map((line) => buildPatchingAmountSubtitle(line, currency))
      .join(' | ');
    doc.text(patchingSubtitle, margin, yPosition);

    yPosition += 5;

    const cycleMonths = quarterlyLine?.quarterCycleMonths ?? [];
    const isQuarterlyTable = cycleMonths.length > 0;
    const monthHeaders = cycleMonths.map((m) => formatPatchingBillingMonthLabel(m));

    const tableHead = isQuarterlyTable
      ? ['Resource', 'Group', ...monthHeaders, 'Total Hrs', 'Rate', 'Amount']
      : ['Resource', 'Group', 'Hours', 'Rate', 'Amount'];

    const tableData: string[][] = [];
    let totalLabor = 0;

    for (const line of typeGroup.lines) {
      for (const row of line.resourceBreakdown || []) {
        totalLabor += row.billableAmount || 0;
        if (isQuarterlyTable) {
          const monthCells = cycleMonths.map((m) =>
            formatBillingHoursDisplay(monthHoursForRow(row, m))
          );
          tableData.push([
            row.userName,
            row.groupName,
            ...monthCells,
            formatBillingHoursDisplay(row.totalHours),
            `${formatBillingCurrency(row.hourlyRate, currency)}/hr`,
            formatBillingCurrency(row.billableAmount, currency)
          ]);
        } else {
          tableData.push([
            row.userName,
            row.groupName,
            formatBillingHoursDisplay(row.totalHours),
            `${formatBillingCurrency(row.hourlyRate, currency)}/hr`,
            formatBillingCurrency(row.billableAmount, currency)
          ]);
        }
      }
    }

    if (tableData.length === 0) {
      const emptyCols = isQuarterlyTable ? cycleMonths.length + 4 : 3;
      tableData.push(['No logged hours for this period', ...Array(emptyCols).fill('')]);
    }

    const totalLaborRounded = sumAndRoundHalfUp2([totalLabor]);
    const totalRow = isQuarterlyTable
      ? ['Total', '', ...cycleMonths.map(() => ''), '', '', formatBillingCurrency(totalLaborRounded, currency)]
      : ['Total', '', '', '', formatBillingCurrency(totalLaborRounded, currency)];
    tableData.push(totalRow);

    const amountColIndex = tableHead.length - 1;
    const rightAlignFrom = isQuarterlyTable ? 2 : 2;

    autoTable(doc, {
      startY: yPosition,
      head: [tableHead],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: isQuarterlyTable ? 8 : 10
      },
      bodyStyles: {
        fontSize: isQuarterlyTable ? 8 : 9,
        textColor: [31, 41, 55]
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left' }
      },
      margin: { left: margin, right: margin, bottom: footerAreaHeight },
      tableWidth,
      didParseCell: (data: any) => {
        if (data.section === 'head' && data.column.index >= rightAlignFrom) {
          data.cell.styles.halign = 'right';
        }
        if (data.section === 'body' && data.column.index >= rightAlignFrom) {
          data.cell.styles.halign = 'right';
        }
        if (data.section === 'body' && data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;
          data.cell.styles.fillColor = [243, 244, 246];
          data.cell.styles.textColor = [31, 41, 55];
          if (data.column.index === amountColIndex) {
            data.cell.styles.halign = 'right';
          }
        }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
    if (yPosition > maxContentY) {
      doc.addPage();
      yPosition = margin;
    }
  }

  return yPosition;
}

function formatMonthHoursBifurcation(
  row: PatchingBillingLine['resourceBreakdown'][0]
): string {
  if (!row.monthlyHours?.length) {
    return '';
  }
  return row.monthlyHours
    .filter((m) => m.totalHours > 0)
    .map((m) => `${formatPatchingBillingMonthLabel(m.month)}: ${formatBillingHoursDisplay(m.totalHours)}`)
    .join('; ');
}

/** CSV rows for patching (one row per resource; quarterly includes month bifurcation in group column). */
export function formatPatchingCsvResourceRows(
  line: PatchingBillingLine,
  fromDate: string,
  toDate: string,
  customerOrEngagementLabel: string
): string[] {
  const rows: string[] = [];
  const chargeType = `patching_${line.patchingType.toLowerCase()}`;
  const cycleLabel = formatPatchingQuarterCycleLabel(
    line.quarterCycleStartMonth,
    line.quarterCycleEndMonth
  );
  const periodLabel =
    line.billingCycle === 'QUARTERLY' && cycleLabel
      ? `${fromDate} - ${toDate} (Quarter: ${cycleLabel})`
      : `${fromDate} - ${toDate}`;

  for (const row of line.resourceBreakdown || []) {
    const bifurcation = formatMonthHoursBifurcation(row);
    const groupLabel = bifurcation ? `${row.groupName} | ${bifurcation}` : row.groupName;

    rows.push(
      [
        customerOrEngagementLabel,
        periodLabel,
        chargeType,
        `"${row.userName.replace(/"/g, '""')}"`,
        `"${groupLabel.replace(/"/g, '""')}"`,
        formatBillingHoursDisplay(row.totalHours),
        formatBillingMoneyPlain(row.hourlyRate),
        formatBillingMoneyPlain(row.billableAmount)
      ].join(',')
    );
  }

  return rows;
}

/** Engagement CSV export row(s) for patching with optional monthly detail rows. */
export function patchingRowsForEngagementCsvExport(
  line: PatchingBillingLine,
  fromDate: string,
  toDate: string
): Array<{
  description: string;
  group: string;
  totalHours: string;
  rate: string;
  totalAmount: string;
}> {
  const cycleLabel = formatPatchingQuarterCycleLabel(
    line.quarterCycleStartMonth,
    line.quarterCycleEndMonth
  );
  const rows: Array<{
    description: string;
    group: string;
    totalHours: string;
    rate: string;
    totalAmount: string;
  }> = [];

  for (const row of line.resourceBreakdown || []) {
    const bifurcation = formatMonthHoursBifurcation(row);
    const baseGroup = `${row.groupName} (${line.billingCycleLabel})`;

    if (line.billingCycle === 'QUARTERLY' && row.monthlyHours?.length) {
      for (const mh of row.monthlyHours) {
        if (mh.totalHours <= 0) continue;
        const partialAmount = sumAndRoundHalfUp2([mh.totalHours * row.hourlyRate]);
        rows.push({
          description: `${row.userName} — ${formatPatchingBillingMonthLabel(mh.month)}`,
          group: baseGroup,
          totalHours: formatBillingHoursDisplay(mh.totalHours),
          rate: formatBillingMoneyPlain(row.hourlyRate),
          totalAmount: formatBillingMoneyPlain(partialAmount)
        });
      }
    }

    rows.push({
      description: row.userName,
      group: bifurcation ? `${baseGroup} | ${bifurcation}` : baseGroup,
      totalHours: formatBillingHoursDisplay(row.totalHours),
      rate: formatBillingMoneyPlain(row.hourlyRate),
      totalAmount: formatBillingMoneyPlain(row.billableAmount)
    });
  }

  if (cycleLabel && rows.length > 0) {
    rows[rows.length - 1].group = `${rows[rows.length - 1].group} | Quarter: ${cycleLabel}`;
  }

  return rows;
}
